import { useMemo } from "react";
import { CustomerInformationDrawerProps } from "../CustomerInformationDrawer";
import getCustomerInformationCardProps from "./getCustomerInformationCardProps";
import useCustomerEditFormContainer from "./useCustomerEditFormContainer";
import { Customer } from "./types";

const useNormalizedCustomerDrawerData = (params: {
  customer: Customer | null;
  isOtherPerson?: boolean;
  handlePatientClick: (patientMrn: string) => void;
}) => {
  const { customer, isOtherPerson, handlePatientClick } = params;

  const customerEditFormContainerProps = useCustomerEditFormContainer({
    customerId: customer?.id,
    isOtherPerson,
  });

  const data = useMemo(() => {
    if (!customer || !customerEditFormContainerProps) return null;

    const result: CustomerInformationDrawerProps["data"] = {
      name: `${customer.last_name}, ${customer.first_name}`,
      mergeStatus: customer.merge_status,
      customerInformationCardProps: getCustomerInformationCardProps({
        customer,
        handlePatientClick,
      }),
      customerEditFormContainerProps,
      auditLogsData: isOtherPerson
        ? null
        : {
            // Interleaved by date — both sources come pre-sorted DESC on
            // their own, but need a single re-sort once combined.
            auditLogs: [
              ...customer.customer_audit_logs,
              ...customer.merge_audit_logs,
            ].sort(
              (a, b) =>
                new Date(b.created_at).getTime() -
                new Date(a.created_at).getTime(),
            ),
            // A customer with no appointments yet (or a merged-away customer,
            // whose appointment_customer_details rows all got re-linked away)
            // has an empty array here — fall back to no timezone rather than
            // crashing (AuditLogList's timezone prop is optional).
            timezone:
              customer.appointment_customer_details[0]?.appointment.center
                .timezone,
          },
    };

    return result;
  }, [
    customer,
    customerEditFormContainerProps,
    handlePatientClick,
    isOtherPerson,
  ]);

  return data;
};

export default useNormalizedCustomerDrawerData;
